import React, {
    useState,
    useRef,
} from "react";
import {
    clearExcelLayoutTemplateCache,
    createTemplate,
    loadScopedTemplates,
    updateTemplate,
} from "../api/excelLayoutTemplates";
import {
    saveConfig,
    updateConfig,
} from "../api/configurations";
import {
    reportToast,
} from "../../../../components/notifications/appToast";
import {
    selectExcelLayoutTemplateFields,
} from "../mapping/excelLayoutTemplateMapping";
import {
    clone,
    columnMatchesKey,
    columnsForModel,
    dataColumnSelectionKey,
    findFolder,
    findPlan,
    findReport,
    findReportColumn,
    findTimestampColumn,
    nextId,
    today,
} from "../reportConfigurationLogic";
import type {
    ChangeEntry,
    DataModel,
    MainTab,
    MetricSection,
    ModelKey,
    PlanConfig,
    ReportConfig,
    ReportConfigurationLookupOption,
    ReportConfigurationExcelLayoutTemplateDto,
    ReportConfigurationState,
    SaveReportConfigurationExcelLayoutTemplatePayload,
    ReportFolder,
    SaveReportConfigurationConfigResultDto,
} from "../types";
import {
    buildReportConfigurationPayload,
} from "../reportConfigurationPersistence";
import {
    validateReportConfiguration,
} from "../reportConfigurationValidation";
import type {
    ReportValidationIssue,
} from "../reportConfigurationValidation";
import {
    stableJson,
} from "./useConfigurationChangeTracking";
import { workbookDownloadErrorMessage } from "../api/workbookDownloads";
import { useWorkbookDownload } from "./useWorkbookDownload";
import type { DownloadHistoryEntry } from "./useWorkbookDownload";

const recentDownloadHistory = (entries: DownloadHistoryEntry[]) => {
    let finished = 0;
    return entries.filter(entry => {
        if (!entry.finishedAt) return true;
        finished += 1;
        return finished <= 20;
    });
};
const downloadKey = (report: Pick<ReportConfig, "reportId" | "configVersionId">) =>
    `${report.reportId}:${report.configVersionId ?? "latest"}`;

type ConfigurationSaveWorkflowInputs = {
    configurationLoading?: boolean;
    models: Record<string, DataModel>;
    lookupOptionsByKey: Record<string, ReportConfigurationLookupOption[]>;
    data: ReportConfigurationState;
    setData: React.Dispatch<React.SetStateAction<ReportConfigurationState>>;
    mainTab: MainTab;
    configSaveLoading: boolean;
    setConfigSaveLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setSavedReportBaselines: React.Dispatch<React.SetStateAction<Record<string, { plan: PlanConfig; folder: ReportFolder; report: ReportConfig; }>>>;
    setValidationIssues: React.Dispatch<React.SetStateAction<ReportValidationIssue[]>>;
    setValidationDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
    selectedPlan: PlanConfig;
    selectedFolder: ReportFolder;
    selectedReport: ReportConfig | undefined;
    saveDisabled: boolean;
    lookups: Record<string, string[]>;
    applyReportConfigurationToState: (current: ReportConfigurationState, targetPlanId: string, targetFolderId: string, targetReportId: string, nextPlan: PlanConfig, nextFolder: ReportFolder, nextReport: ReportConfig, changeEntry?: ChangeEntry | undefined) => ReportConfigurationState;
    errorMessage: (error: any, fallback: string) => any;
    reportBaselinePending: boolean;
    noReportChangesToSave: boolean;
    dataModelIdForKey: (modelKey: string) => number | undefined;
    getFrequencyOptions: () => ReportConfigurationLookupOption[];
    getReportingPeriodOptions: () => ReportConfigurationLookupOption[] | { id: string; value: string; name: string; }[];
    getTripActivityOptions: () => ReportConfigurationLookupOption[];
    getClientOptions: () => ReportConfigurationLookupOption[];
    onSave: ((payload: ReportConfigurationState) => void | Promise<void>) | undefined;
};

export const useConfigurationSaveWorkflow = ({
    models,
    lookupOptionsByKey,
    data,
    setData,
    mainTab,
    configSaveLoading,
    setConfigSaveLoading,
    setSavedReportBaselines,
    setValidationIssues,
    setValidationDialogOpen,
    selectedPlan,
    selectedFolder,
    selectedReport,
    saveDisabled,
    lookups,
    applyReportConfigurationToState,
    errorMessage,
    reportBaselinePending,
    noReportChangesToSave,
    dataModelIdForKey,
    getFrequencyOptions,
    getReportingPeriodOptions,
    getTripActivityOptions,
    getClientOptions,
    onSave,
    configurationLoading = false,
}: ConfigurationSaveWorkflowInputs) => {
    const prepareDownload = useWorkbookDownload(configurationLoading);
    const [downloadHistory, setDownloadHistory] = useState<DownloadHistoryEntry[]>([]);
    const downloadSequence = useRef(0);
    const activeDownloads = useRef(new Set<string>());
    const clearDownloadHistory = () => setDownloadHistory(entries => entries.filter(entry => !entry.finishedAt));
    const getWorkbookDownload = (report: ReportConfig) => downloadHistory.find(entry =>
        entry.reportId === report.reportId && entry.configVersionId === (report.configVersionId ?? null));
    const [templateRefreshToken, setTemplateRefreshToken] = useState(0);

    const dataWithResolvedDataModelIds = (current: ReportConfigurationState): ReportConfigurationState => ({
        ...current,
        plans: current.plans.map((plan) => ({
            ...plan,
            reportFolders: plan.reportFolders.map((folder) => ({
                ...folder,
                reports: folder.reports.map((report) => {
                    const modelBasedOn = report.models.reduce((acc, modelKey) => {
                        const model = models[modelKey];
                        const basedOn = findTimestampColumn(model, report.modelBasedOn[modelKey]);
                        const basedOnKey = dataColumnSelectionKey(basedOn);
                        if (basedOnKey) acc[modelKey] = basedOnKey;
                        return acc;
                    }, {} as Partial<Record<ModelKey, string>>);

                    return {
                        ...report,
                        dataModelIds: report.models
                            .map((modelKey) => dataModelIdForKey(modelKey))
                            .filter((id): id is number => !!id),
                        modelBasedOn,
                    };
                }),
            })),
        })),
    });

    const defaultTemplateName = (title: string) => `Template - ${(title || "Untitled Metric").trim() || "Untitled Metric"}`;
    const normalizedTemplateName = (value: string) => value.trim().toLowerCase();
    const comparableTemplateDefinition = (json: string) => {
        try {
            const parsed = JSON.parse(json || "{}") as Record<string, unknown>;
            const { title, blockOrder, enabled, sheetName, ...layoutDefinition } = parsed;
            return stableJson(layoutDefinition);
        } catch {
            return (json || "").trim();
        }
    };
    const templateDefinitionJson = (section: MetricSection) => {
        const layout = section.excelLayout;
        if (!layout) return "";
        const { sheetName, enabled, ...definition } = selectExcelLayoutTemplateFields(layout);
        return JSON.stringify({ ...definition, extraBreakdowns: section.extraBreakdowns });
    };
    const metricTemplateColumnId = (report: ReportConfig, section: MetricSection) => {
        const reportColumn = findReportColumn(report, section.breakdownColumn);
        if (reportColumn?.dataModelColumnId) return reportColumn.dataModelColumnId;
        const modelColumn = columnsForModel(section.model).find((column) => columnMatchesKey(column, section.breakdownColumn));
        return modelColumn?.dataModelColumnId || null;
    };
    const applyTemplateSaveResult = (report: ReportConfig, sectionId: string, templateId: number, templateName: string): ReportConfig => ({
        ...report,
        sections: report.sections.map((section) => section.id === sectionId && section.excelLayout ? {
            ...section,
            excelLayout: {
                ...section.excelLayout,
                templateId,
                templateName,
                templateNameTouched: true,
                sourceTemplateName: templateName,
                sourceTemplateIsGlobal: false,
                saveAsTemplate: false,
                templateSaveMode: "create",
            },
        } : section),
    });
    const savePendingExcelLayoutTemplates = async (report: ReportConfig) => {
        let nextReport = report;
        let savedCount = 0;

        for (const section of report.sections) {
            const layout = section.excelLayout;
            if (!layout?.saveAsTemplate) continue;

            const dataModelId = section.model ? dataModelIdForKey(section.model) : null;
            const dataModelColumnId = metricTemplateColumnId(report, section);
            if (!dataModelId || !dataModelColumnId) {
                throw new Error(`Template cannot be saved for "${section.name || "Untitled Metric"}" until its Data Model and column are loaded from the backend.`);
            }

            const payload: SaveReportConfigurationExcelLayoutTemplatePayload = {
                dataModelId,
                dataModelColumnId,
                reportId: report.reportId ?? null,
                displayAs: section.displayAs,
                isGlobalTemplate: false,
                templateName: (layout.templateName || defaultTemplateName(layout.title || section.name)).trim(),
                templateDefinitionJson: templateDefinitionJson(section),
            };

            if (!payload.templateName) {
                throw new Error(`Template name is required for "${section.name || "Untitled Metric"}".`);
            }

            const templateIdForUpdate = layout.templateId ? Number(layout.templateId) : null;
            const shouldUpdateTemplate = !!templateIdForUpdate && layout.templateSaveMode === "update";
            const existingTemplates = await loadScopedTemplates(dataModelId, dataModelColumnId, section.displayAs, { reportId: report.reportId, scope: "report" });
            const selectedTemplate = templateIdForUpdate
                ? existingTemplates.find((template) => Number(template.templateId) === templateIdForUpdate)
                : null;

            if (shouldUpdateTemplate && (layout.sourceTemplateIsGlobal || selectedTemplate?.isGlobalTemplate)) {
                throw new Error(`Global template "${layout.sourceTemplateName || layout.templateName || "Selected Template"}" can only be updated from the Global Templates tab. Change the template name to save a report-level copy.`);
            }

            if (layout.templateId && !shouldUpdateTemplate && selectedTemplate
                && comparableTemplateDefinition(payload.templateDefinitionJson) === comparableTemplateDefinition(selectedTemplate.templateDefinitionJson)) {
                throw new Error(`Make layout changes before saving a new template for "${section.name || "Untitled Metric"}", or use Update Selected Template.`);
            }

            const isKeepingSelectedTemplateName = shouldUpdateTemplate
                && selectedTemplate
                && normalizedTemplateName(payload.templateName) === normalizedTemplateName(String(selectedTemplate.templateName || ""));
            const duplicateTemplate = Array.isArray(existingTemplates) && !isKeepingSelectedTemplateName
                ? existingTemplates.find((template) =>
                    normalizedTemplateName(String(template.templateName || "")) === normalizedTemplateName(payload.templateName)
                    && (!shouldUpdateTemplate || Number(template.templateId) !== templateIdForUpdate))
                : null;

            if (duplicateTemplate) {
                const isSelectedTemplateName = templateIdForUpdate && Number(duplicateTemplate.templateId) === templateIdForUpdate;
                throw new Error(isSelectedTemplateName
                    ? `Change the template name before saving "${section.name || "Untitled Metric"}" as a new template.`
                    : `Template name "${payload.templateName}" already exists. Please use a different template name.`);
            }

            const response = shouldUpdateTemplate
                ? await updateTemplate(templateIdForUpdate, payload)
                : await createTemplate(payload);
            const savedTemplateId = Number(response?.templateId || layout.templateId || 0);
            const savedTemplateName = String(response?.templateName || payload.templateName);
            if (!savedTemplateId) {
                throw new Error(`Template save did not return a TemplateId for "${payload.templateName}".`);
            }

            clearExcelLayoutTemplateCache(dataModelId, dataModelColumnId, section.displayAs);
            nextReport = applyTemplateSaveResult(nextReport, section.id, savedTemplateId, savedTemplateName);
            savedCount += 1;
        }

        return {
        report: nextReport,
        savedCount,
    };
    };

    const saveForTab = async (mainTab: MainTab): Promise<boolean> => {
        if (saveDisabled) {
            reportToast.warning("Save is not ready", "Backend data models and report lookup values must load before saving.");
            return false;
        }
        if (configSaveLoading) return false;
        if (reportBaselinePending) {
            reportToast.info("Report configuration is still loading", "Please wait before saving this report configuration.");
            return false;
        }
        const label = mainTab === "plan" ? selectedPlan.name : selectedReport ? `${selectedFolder?.label || "Reports"}|${selectedReport.label}` : selectedPlan.name;
        const start = mainTab === "plan" ? today : selectedReport?.effectiveStart || today;
        const entry: ChangeEntry = {
            id: nextId("change"),
            level: mainTab === "plan" ? "Plan" : "Report",
            label,
            status: start > today ? "Scheduled" : "Active",
            start,
            end: "ongoing",
            user: "You",
            ts: "Just now",
            changes: [mainTab === "plan" ? "Plan Settings Updated" : "Report Configuration Updated"],
        };

        const resolvedData = dataWithResolvedDataModelIds(data);

        if (mainTab !== "reports") {
            const payload = { ...resolvedData, changes: [entry, ...resolvedData.changes] };
            setData(payload);
            await onSave?.(payload);
            reportToast.success("Plan settings staged", "The current plan changes are reflected in the configurator.");
            return true;
        }

        if (!selectedFolder || !selectedReport) {
            const message = "Select a report before saving.";
            reportToast.warning("No report selected", message);
            return false;
        }

        if (mainTab === "reports") {
            const issues = validateReportConfiguration(selectedPlan, selectedFolder, selectedReport, {
                dataModels: models,
                frequencyOptions: getFrequencyOptions(),
                reportingPeriodOptions: getReportingPeriodOptions(),
                tripActivityOptions: getTripActivityOptions(),
            });
            const blockingIssues = issues.filter((issue) => issue.severity === "error");
            if (blockingIssues.length) {
                setValidationIssues(issues);
                setValidationDialogOpen(true);
                return false;
            }
            setValidationIssues(issues);
        }

        if (noReportChangesToSave) {
            return false;
        }

        if (!selectedPlan.reportPlanId || !selectedFolder.reportFolderId || !selectedReport.reportId) {
            const message = "Report metadata has not loaded yet. Please wait before saving.";
            reportToast.warning("Report metadata is still loading", message);
            return false;
        }

        setConfigSaveLoading(true);

        const toastId = reportToast.loading(
            "Saving report configuration...",
            `${selectedFolder.label} / ${selectedReport.label}`,
        );
        try {
            const resolvedPlan = findPlan(resolvedData, selectedPlan.id);
            const resolvedFolder = findFolder(resolvedPlan, selectedFolder.id);
            const resolvedReport = findReport(resolvedFolder, selectedReport.id);

            if (!resolvedFolder || !resolvedReport) {
                throw new Error("Selected report was not found in the current configuration.");
            }

            const templateResult = await savePendingExcelLayoutTemplates(resolvedReport);
            const reportForSave = templateResult.report;
            const folderForSave = {
                ...resolvedFolder,
                reports: resolvedFolder.reports.map((report) => report.id === reportForSave.id ? reportForSave : report),
            };
            const planForSave = {
                ...resolvedPlan,
                reportFolders: resolvedPlan.reportFolders.map((folder) => folder.id === folderForSave.id ? folderForSave : folder),
            };
            const built = buildReportConfigurationPayload({
                plan: planForSave,
                folder: folderForSave,
                report: reportForSave,
                dataModels: models,
                lookupOptionsByKey,
                lookupValues: lookups,
                clientOptions: getClientOptions(),
            });
            const existingConfigVersionId = built.report.configVersionId || resolvedReport.configVersionId || null;
            const payloadToSave = existingConfigVersionId
                ? { ...built.payload, configVersionId: existingConfigVersionId, operation: "updateDraft" }
                : built.payload;
            const response = (existingConfigVersionId
                ? await updateConfig(existingConfigVersionId, payloadToSave)
                : await saveConfig(payloadToSave)) as SaveReportConfigurationConfigResultDto;
            const savedConfigVersionId = Number(response?.configVersionId || existingConfigVersionId);

            if (!savedConfigVersionId) {
                throw new Error("The save API did not return a ConfigVersionId.");
            }

            const savedReport = { ...built.report, configVersionId: savedConfigVersionId };
            const payload = applyReportConfigurationToState(
                resolvedData,
                selectedPlan.id,
                selectedFolder.id,
                selectedReport.id,
                built.plan,
                built.folder,
                savedReport,
                entry,
            );

            setData(payload);
            setSavedReportBaselines((current) => ({
                ...current,
                [selectedReport.id]: {
                    plan: clone(built.plan),
                    folder: clone(built.folder),
                    report: clone(savedReport),
                },
            }));
            if (templateResult.savedCount) {
                setTemplateRefreshToken((current) => current + 1);
            }
            reportToast.updateSuccess(
                toastId,
                `Configuration #${savedConfigVersionId} saved`,
                templateResult.savedCount
                    ? `${selectedReport.label} (${templateResult.savedCount} template${templateResult.savedCount === 1 ? "" : "s"} saved)`
                    : selectedReport.label,
            );
            await onSave?.(payload);
            return true;
        } catch (error) {
            console.log(error);
            const message = errorMessage(error, "Unable to save the report configuration.");
            reportToast.updateError(toastId, "Configuration was not saved", message);
            return false;
        } finally {
            setConfigSaveLoading(false);
        }
    };

    const downloadWorkbook = async (report: Pick<ReportConfig, "reportId" | "configVersionId" | "label">, scopeLabel?: string) => {
        const key = downloadKey(report);
        if (activeDownloads.current.has(key)) return;
        if (!report.reportId) {
            const message = "Report metadata has not loaded yet. Please wait before downloading.";
            reportToast.warning("Workbook cannot be generated yet", message);
            return;
        }

        activeDownloads.current.add(key);
        const downloadId = ++downloadSequence.current;
        const reportId = report.reportId;
        const configVersionId = report.configVersionId ?? null;
        const label = report.label || "Report";
        setDownloadHistory(entries => recentDownloadHistory([{ id: downloadId, reportId, configVersionId,
            label, scopeLabel, stage: "submitting", startedAt: Date.now() }, ...entries]));
        const updateDownload = (update: Partial<DownloadHistoryEntry>) => setDownloadHistory(entries =>
            recentDownloadHistory(entries.map(entry => entry.id === downloadId ? { ...entry, ...update } : entry)));

        const reportDescription = scopeLabel ? `${scopeLabel} / ${label}` : label;
        let queuedToastId: string | number | undefined;
        const dismissQueuedToast = () => {
            if (queuedToastId !== undefined) reportToast.dismiss(queuedToastId);
        };
        try {
            const response = await prepareDownload(reportId, configVersionId, () => {}, progress => {
                updateDownload(progress);
                // Only acknowledge the queue once the server has accepted the request.
                if (progress.stage !== "submitting" && queuedToastId === undefined) {
                    queuedToastId = reportToast.info("Report download queued",
                        `${reportDescription}. Check Downloads next to Preview for progress.`);
                }
            });
            const blob = response.data as Blob;
            const contentDisposition = response.headers?.["content-disposition"] || "";
            const fileNameMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
            const fileName = fileNameMatch?.[1] || `${label}.xlsx`;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
            updateDownload({ stage: "completed", percent: 100, finishedAt: Date.now() });
            dismissQueuedToast();
            reportToast.success("Report downloaded", `${reportDescription}. ${fileName}`);
        } catch (error) {
            if ((error as any)?.name === "AbortError" || (error as any)?.code === "ERR_CANCELED") {
                updateDownload({ stage: "cancelled", finishedAt: Date.now() });
                dismissQueuedToast();
                return;
            }
            console.log(error);
            let message = workbookDownloadErrorMessage(error);
            const errorBlob = (error as any)?.response?.data;
            if (errorBlob instanceof Blob) {
                const serverMessage = await errorBlob.text();
                if (serverMessage.includes("Full Row Detail") && serverMessage.includes("aggregate metrics")) {
                    message = "Workbook download blocked: move Full Row Detail sections to a separate sheet from aggregate metrics.";
                }
            }
            updateDownload({ stage: "failed", error: message, finishedAt: Date.now() });
            dismissQueuedToast();
            reportToast.error("Report download failed", `${reportDescription}. ${message}`);
        } finally {
            activeDownloads.current.delete(key);
        }
    };
    return {
        downloadHistory,
        clearDownloadHistory,
        getWorkbookDownload,
        retryWorkbookDownload: (entry: DownloadHistoryEntry) => downloadWorkbook({
            reportId: entry.reportId, configVersionId: entry.configVersionId ?? undefined, label: entry.label,
        }, entry.scopeLabel),
        templateRefreshToken,
        setTemplateRefreshToken,
        metricTemplateColumnId,
        saveCurrent: () => saveForTab(mainTab),
        saveReportForNavigation: () => saveForTab("reports"),
        downloadWorkbook,
    };
};
